import { Link, useLocation } from 'react-router-dom';
import { ArrowUpRight, ChevronDown, LogOut, Search, Settings, Shield, User } from 'lucide-react';
import { useI18n } from '@/hooks/useI18n';
import { useAuth } from '@/contexts/AuthContext';
import { canManageWorkspace, userRoleLabel } from '@/lib/permissions';
import type { Translate } from '@/lib/i18n';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { LanguageSwitcher } from './LanguageSwitcher';
import { MobileNavigation } from './MobileNavigation';
import { useCommandPalette } from './useCommandPalette';

export function TopBar() {
  const { t } = useI18n();
  const { pathname } = useLocation();
  const breadcrumb = resolveBreadcrumb(pathname, t);
  const { open } = useCommandPalette();

  return (
    <header className="sticky top-0 z-20 flex min-h-[68px] items-center justify-between gap-2 border-b border-line-1 bg-bg-1/95 px-3 py-2 backdrop-blur sm:px-6 lg:px-8">
      <div className="flex min-w-0 items-center gap-2 sm:gap-3">
        <MobileNavigation />
        <nav
          aria-label={t('Fil d’Ariane', 'Breadcrumb')}
          className="flex min-w-0 items-center gap-2 text-[12px] sm:gap-3"
        >
          <span className="hidden text-text-3 sm:inline">{breadcrumb.parent}</span>
          <span className="hidden text-line-3 sm:inline" aria-hidden="true">
            /
          </span>
          <span className="truncate font-medium text-text-1">{breadcrumb.current}</span>
        </nav>
      </div>
      <div className="flex shrink-0 items-center gap-1.5 sm:gap-3">
        <button
          type="button"
          onClick={open}
          aria-label={t('Rechercher', 'Search')}
          className="flex size-11 items-center justify-center rounded-[10px] text-text-2 hover:bg-bg-2 lg:hidden"
        >
          <Search className="size-4" />
        </button>
        <Link
          to="/analytics"
          className="hidden items-center gap-1 text-[12px] font-medium text-text-2 transition-colors hover:text-violet-deep md:flex"
        >
          {t('Explorer les données', 'Explore the data')}
          <ArrowUpRight className="size-3.5" aria-hidden="true" />
        </Link>
        <span className="hidden h-5 w-px bg-line-1 md:block" aria-hidden="true" />
        <LanguageSwitcher compact />
        <UserPill />
      </div>
    </header>
  );
}

function resolveBreadcrumb(pathname: string, t: Translate) {
  const titles: Record<string, { parent: string; current: string }> = {
    '/': { parent: t('Mon espace', 'My workspace'), current: t('Aperçu', 'Overview') },
    '/templates': { parent: t('Bibliothèque', 'Library'), current: 'Templates' },
    '/analytics': { parent: t('Tableau de bord', 'Dashboard'), current: 'Analytics' },
    '/accounts': { parent: t('Bibliothèque', 'Library'), current: t('Comptes', 'Accounts') },
    '/identities': {
      parent: t('Administration', 'Administration'),
      current: t('Identités', 'Identities'),
    },
    '/settings': { parent: t('Mon espace', 'My workspace'), current: t('Paramètres', 'Settings') },
    '/ops': {
      parent: t('Administration', 'Administration'),
      current: t('Exploitation', 'Operations'),
    },
  };
  if (titles[pathname]) return titles[pathname];
  if (pathname.startsWith('/accounts/'))
    return { parent: t('Comptes', 'Accounts'), current: t('Profil', 'Profile') };
  return { parent: t('Mon espace', 'My workspace'), current: t('Aperçu', 'Overview') };
}

function UserPill() {
  const { t } = useI18n();
  const { user, logout } = useAuth();
  if (!user) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t('Menu du compte', 'Account menu')}
          className="flex h-11 items-center gap-1.5 rounded-full border border-line-1 bg-bg-1 pl-1 pr-2 transition-colors hover:border-violet/30 sm:h-9 sm:pl-0.5"
        >
          <Avatar className="size-8">
            <AvatarFallback className="bg-violet-soft text-[11px] font-semibold text-violet-deep">
              {user.username.charAt(0).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <ChevronDown className="size-3 text-text-3" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <Link to="/" className="block px-2 py-2 text-left">
          <div className="truncate text-[13px] font-medium text-text-1">{user.username}</div>
          <div className="mt-1 flex items-center gap-1 text-[11px] text-text-3">
            {canManageWorkspace(user.role) ? (
              <Shield className="size-3" />
            ) : (
              <User className="size-3" />
            )}
            {userRoleLabel(user.role, t)}
          </div>
        </Link>
        {canManageWorkspace(user.role) && (
          <DropdownMenuItem asChild>
            <Link to="/settings">
              <Settings className="mr-2 size-3.5" />
              {t('Paramètres', 'Settings')}
            </Link>
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem className="text-rose focus:text-rose" onClick={logout}>
          <LogOut className="mr-2 size-3.5" />
          {t('Se déconnecter', 'Sign out')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
